import { Alert } from "@/src/lib/productAlert";
import { supportsMediaEditing } from "@/src/lib/platformCapabilities";
import { InsetModal as Modal } from "@/src/components/ui/InsetModal";
import { Text, TextInput as BrandTextInput } from "@/src/components/ui/BrandText";
import { border, brand, surface, text as uiText } from "@/src/theme/tokens";
import * as DocumentPicker from "expo-document-picker";
import * as ImagePicker from "expo-image-picker";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useIsFocused } from "@react-navigation/native";
import { deleteDoc, doc, updateDoc } from "firebase/firestore";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  Image,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  View,
  type ViewToken,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { router } from "expo-router";

import { PostCard } from "../../src/components/PostCard";
import { PendingPostCard } from "../../src/components/PendingPostCard";
import { SponsoredPostCard } from "../../src/components/SponsoredPostCard";
import { SPONSORED_FEED_CONFIG, SPONSORED_VISIBILITY_CONFIG } from "../../src/config/sponsoredFeed";
import { contentMaxWidths, readingContent } from "@/src/theme/layout";
import { usePosts } from "../../src/hooks/usePosts";
import { useProfile } from "../../src/hooks/useProfile";
import { useActiveAccount } from "../../src/hooks/useActiveAccount";
import { useSponsoredFeed } from "../../src/hooks/useSponsoredFeed";
import { auth, db } from "../../src/lib/firebase";
import { blockUser } from "../../src/lib/blocks";
import {
  cleanupCurrentPostImages,
  cleanupCurrentPostPdf,
  createPostUploadError,
  formatAttachmentSize,
  MAX_POST_IMAGE_BYTES,
  pendingImagesFromAssets,
  uploadPostImages,
  uploadPostPdf,
  type PendingAttachment,
} from "../../src/lib/postAttachments";
import {
  buildPostImageEdit,
  getPostImageUrls,
} from "../../src/lib/postImages";
import {
  buildPostEditPdfStoragePath,
  buildPostEditPayload,
  postEditHasChanges,
} from "../../src/lib/postEditing";
import {
  firebaseErrorDetails,
  safePostErrorMessage,
  validateCanonicalPostProfile,
} from "../../src/lib/postPublishing";
import { reportPost, type ReportReason } from "../../src/lib/reports";
import { useBlockedUsers } from "../../src/hooks/useBlockedUsers";
import { SUBJECTS, SUBJECT_LABEL } from "../../src/lib/taxonomy";
import { usePostPublishQueue } from "../../src/hooks/usePostPublishQueue";
import { useMyPostLikes } from "../../src/hooks/useMyPostLikes";
import { togglePostLike } from "../../src/lib/postLikes";
import { injectSponsoredItems } from "../../src/lib/sponsoredFeed";
import { canPublishDocuments, getAuthorAccountType } from "../../src/lib/identity";
import type { FirePost } from "../../src/types/models";
import type { MixedFeedItem } from "../../src/types/sponsored";

const BG = surface.secondary;
const ACCENT = brand.primary;
const MAX_POST_IMAGES = 6;
const REPORT_REASONS: { value: ReportReason; label: string }[] = [
  { value: "sexual_content", label: "Contenu sexuel" },
  { value: "minor_safety", label: "Sécurité d'un mineur" },
  { value: "violence", label: "Violence" },
  { value: "harassment", label: "Harcèlement" },
  { value: "hate", label: "Haine" },
  { value: "spam", label: "Spam" },
  { value: "impersonation", label: "Usurpation" },
  { value: "off_topic", label: "Hors sujet" },
  { value: "privacy", label: "Vie privée" },
  { value: "other", label: "Autre" },
];
const REPORTED_POSTS_STORAGE_PREFIX = "classti:reported-posts:";

type FeedPost = FirePost & {
  liked: boolean;
  likes: number;
  isMine: boolean;
  subjectLabel: string;
};

function getEditableImageUrls(item: any) {
  return getPostImageUrls(item);
}

export default function HomeScreen() {
  const user = auth.currentUser;
  const isFocused = useIsFocused();
  const { loadingProfile, profile } = useProfile(user);
  const { activeAccountId } = useActiveAccount();
  const blockedUserIds = useBlockedUsers(user?.uid);

  const subjects = [
    "all",
    ...SUBJECTS.map((s) => s.value).filter((v) => v !== "general"),
  ] as const;

  const [selectedSubject, setSelectedSubject] =
    useState<(typeof subjects)[number]>("all");
  const [teachersOnly, setTeachersOnly] = useState(false);
  const [followingOnly, setFollowingOnly] = useState(false);

  const {
    posts,
    loadingPosts,
    loadingMore,
    hasMore,
    error,
    errorRetryable,
    offline,
    reload,
    loadMore,
  } = usePosts(user, isFocused, followingOnly, activeAccountId);
  const {
    items: sponsoredItems,
    recordImpression: recordSponsoredImpression,
    recordClick: recordSponsoredClick,
  } = useSponsoredFeed(
    user?.uid,
    isFocused,
    selectedSubject === "all" ? undefined : selectedSubject
  );
  const { likedPostIds, setLikedPostIds } = useMyPostLikes(
    posts.map((post) => post.id),
    user?.uid
  );
  const {
    jobs: publishJobs,
    reconcileRemote,
    retry: retryPublish,
    cancel: cancelPublish,
    remove: removePublish,
  } = usePostPublishQueue();
  useEffect(() => {
    void reconcileRemote(posts.map((post) => ({
      id: post.id,
      authorUid: post.authorUid,
      moderationStatus: post.moderationStatus,
    })));
  }, [posts, reconcileRemote]);

  const localPublishJobs = useMemo(() => {
    const remoteIds = new Set(posts.map((post) => post.id));
    return publishJobs.filter((job) => job.status !== "canceled" && !remoteIds.has(job.postId));
  }, [posts, publishJobs]);

  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    let alive = true;
    const storageKey = user?.uid ? `${REPORTED_POSTS_STORAGE_PREFIX}${user.uid}` : "";

    if (!storageKey) {
      setHiddenIds(new Set());
      return () => {
        alive = false;
      };
    }

    AsyncStorage.getItem(storageKey)
      .then((raw) => {
        if (!alive) return;
        try {
          const ids = JSON.parse(raw ?? "[]");
          setHiddenIds(new Set(Array.isArray(ids) ? ids.filter((id) => typeof id === "string") : []));
        } catch {
          setHiddenIds(new Set());
        }
      })
      .catch(() => {
        if (alive) setHiddenIds(new Set());
      });

    return () => {
      alive = false;
    };
  }, [user?.uid]);

  const [likesOverlay, setLikesOverlay] = useState<
    Record<string, { likedBy: string[]; likesCount: number }>
  >({});
  const [textOverlay, setTextOverlay] = useState<Record<string, string>>({});
  const [selectedPost, setSelectedPost] = useState<any | null>(null);
  const [reportingPost, setReportingPost] = useState<any | null>(null);
  const [reportReason, setReportReason] = useState<ReportReason>("other");
  const [reportDetails, setReportDetails] = useState("");
  const [submittingReport, setSubmittingReport] = useState(false);
  const [editingPost, setEditingPost] = useState<any | null>(null);
  const [editingText, setEditingText] = useState("");
  const [editingImages, setEditingImages] = useState<string[]>([]);
  const [editingNewImages, setEditingNewImages] = useState<PendingAttachment[]>([]);
  const [editingPdfUrl, setEditingPdfUrl] = useState("");
  const [editingPdfName, setEditingPdfName] = useState("");
  const [editingNewPdf, setEditingNewPdf] = useState<PendingAttachment | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const optimisticHide = useCallback((postId: string) => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      next.add(postId);
      return next;
    });
  }, []);

  const optimisticUnhide = useCallback((postId: string) => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      next.delete(postId);
      return next;
    });
  }, []);

  const persistReportedPost = useCallback(async (postId: string) => {
    if (!user?.uid) return;
    const storageKey = `${REPORTED_POSTS_STORAGE_PREFIX}${user.uid}`;
    try {
      const raw = await AsyncStorage.getItem(storageKey);
      const ids = new Set<string>(
        (() => {
          try {
            const parsed = JSON.parse(raw ?? "[]");
            return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
          } catch {
            return [];
          }
        })()
      );
      ids.add(postId);
      await AsyncStorage.setItem(storageKey, JSON.stringify(Array.from(ids)));
    } catch {
      // Le masquage immédiat reste assuré par l'état local de l'écran.
    }
  }, [user?.uid]);

  const visiblePosts = useMemo<FeedPost[]>(() => {
    const u = auth.currentUser;

    return posts
      .filter((p: any) => {
        if (hiddenIds.has(p.id)) return false;
        if (blockedUserIds.has(String(p.authorUid ?? ""))) return false;
        if (
          teachersOnly
          && getAuthorAccountType(p) !== "teacher"
        ) return false;
        if (selectedSubject === "all") return true;
        if (!p.subject) return false;
        return p.subject === selectedSubject;
      })
      .map((p: any) => {
        const o = likesOverlay[p.id];
        const merged = o
          ? { ...p, likedBy: o.likedBy, likesCount: o.likesCount }
          : p;
        const nextText = textOverlay[p.id];
        const mergedWithText =
          typeof nextText === "string" ? { ...merged, text: nextText } : merged;

        const likedByArr: string[] = Array.isArray(mergedWithText.likedBy)
          ? mergedWithText.likedBy
          : [];
        const liked = !!u && (likedPostIds.has(mergedWithText.id) || likedByArr.includes(u.uid));
        const likes = Number.isInteger(mergedWithText.likesCount)
          ? mergedWithText.likesCount
          : likedByArr.length;
        const isMine = !!u && mergedWithText.authorUid === u.uid;

        return {
          ...mergedWithText,
          liked,
          likes,
          isMine,
          subjectLabel: mergedWithText.subject
            ? SUBJECT_LABEL[mergedWithText.subject as keyof typeof SUBJECT_LABEL] ??
              mergedWithText.subject
            : "General",
        } as FeedPost;
      });
  }, [blockedUserIds, hiddenIds, likedPostIds, likesOverlay, posts, selectedSubject, teachersOnly, textOverlay]);

  const feedItems = useMemo(
    () => injectSponsoredItems(visiblePosts, sponsoredItems, SPONSORED_FEED_CONFIG),
    [sponsoredItems, visiblePosts]
  );
  const sponsoredImpressionHandler = React.useRef(recordSponsoredImpression);
  sponsoredImpressionHandler.current = recordSponsoredImpression;
  const onViewableItemsChanged = React.useRef(
    ({ viewableItems }: { viewableItems: ViewToken<MixedFeedItem<FeedPost>>[] }) => {
      for (const token of viewableItems) {
        if (token.isViewable && token.item.type === "sponsored") {
          sponsoredImpressionHandler.current(token.item);
        }
      }
    }
  ).current;

  const deletePost = useCallback(
    async (postId: string) => {
      optimisticHide(postId);

      try {
        await deleteDoc(doc(db, "posts", postId));
      } catch {
        optimisticUnhide(postId);
        Alert.alert("Erreur", "Impossible de supprimer le post.");
      }
    },
    [optimisticHide, optimisticUnhide]
  );

  const closeActionSheet = useCallback(() => {
    setSelectedPost(null);
  }, []);

  const openPostActions = useCallback((item: any) => {
    setSelectedPost(item);
  }, []);

  const openEditPost = useCallback(() => {
    if (!selectedPost) return;
    const postToEdit = selectedPost;
    Alert.alert(
      "Nouvelle validation nécessaire",
      "Toute modification remet ce post en validation. Il pourra être temporairement invisible du feed jusqu'à son approbation.",
      [
        { text: "Annuler", style: "cancel" },
        {
          text: "Modifier",
          onPress: () => {
            setEditingPost(postToEdit);
            setEditingText(String(postToEdit.text ?? ""));
            setEditingImages(getEditableImageUrls(postToEdit));
            setEditingNewImages([]);
            setEditingPdfUrl(
              postToEdit?.attachmentType === "pdf" ? String(postToEdit.attachmentUrl ?? "") : ""
            );
            setEditingPdfName(
              postToEdit?.attachmentType === "pdf" ? String(postToEdit.attachmentName ?? "") : ""
            );
            setEditingNewPdf(null);
            setSelectedPost(null);
          },
        },
      ]
    );
  }, [selectedPost]);

  const closeEditPost = useCallback(() => {
    if (savingEdit) return;
    setEditingPost(null);
    setEditingText("");
    setEditingImages([]);
    setEditingNewImages([]);
    setEditingPdfUrl("");
    setEditingPdfName("");
    setEditingNewPdf(null);
  }, [savingEdit]);

  const pickEditImages = useCallback(async () => {
    try {
      const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!permission.granted) {
        Alert.alert("Permission", "Autorise l'acces aux photos pour continuer.");
        return;
      }

      const currentCount = editingImages.length + editingNewImages.length;
      const remainingSlots = MAX_POST_IMAGES - currentCount;

      if (remainingSlots <= 0) {
      Alert.alert("Limite atteinte", `Tu peux ajouter jusqu'à ${MAX_POST_IMAGES} photos.`);
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ["images"],
        allowsMultipleSelection: true,
        selectionLimit: remainingSlots,
        quality: 1,
        exif: false,
      });

      if (result.canceled || !result.assets?.length) return;

      const { attachments, rejectedTooLarge } = pendingImagesFromAssets(
        result.assets,
        remainingSlots
      );

      if (rejectedTooLarge > 0) {
        Alert.alert(
          "Photo trop lourde",
          `Chaque photo doit faire moins de ${formatAttachmentSize(MAX_POST_IMAGE_BYTES)}.`
        );
      }

      if (attachments.length === 0) return;

      setEditingNewImages((prev) =>
        [...prev, ...attachments].slice(0, MAX_POST_IMAGES - editingImages.length)
      );
    } catch (e: any) {
      const error = createPostUploadError(
        "post/image-picker-failed",
        "Impossible d'ajouter cette photo.",
        e
      );
      console.error("upload:image:error", {
        ...firebaseErrorDetails(error),
        stage: "image-picker",
      });
      Alert.alert("Erreur", safePostErrorMessage(error, "Impossible d’ajouter cette photo."));
    }
  }, [editingImages.length, editingNewImages.length]);

  const pickEditPdf = useCallback(async () => {
    if (!canPublishDocuments(profile)) {
      Alert.alert("Indisponible", "Seuls les professeurs et les institutions peuvent joindre un PDF.");
      return;
    }

    const result = await DocumentPicker.getDocumentAsync({
      type: "application/pdf",
      copyToCacheDirectory: true,
      multiple: false,
    });

    if (result.canceled || !result.assets?.length) return;

    const asset = result.assets[0];
    setEditingNewPdf({
      uri: asset.uri,
      type: "pdf",
      name: asset.name ?? `document_${Date.now()}.pdf`,
      mimeType: asset.mimeType ?? "application/pdf",
      size: asset.size,
    });
    setEditingPdfUrl("");
    setEditingPdfName("");
  }, [profile]);

  const removeExistingEditImageAt = useCallback((index: number) => {
    setEditingImages((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
  }, []);

  const removeNewEditImageAt = useCallback((index: number) => {
    setEditingNewImages((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
  }, []);

  const clearEditPdf = useCallback(() => {
    setEditingPdfUrl("");
    setEditingPdfName("");
    setEditingNewPdf(null);
  }, []);

  const submitEditPost = useCallback(async () => {
    if (!editingPost || savingEdit || !user) return;

    const cleanText = editingText.trim();
    const hasAttachmentsAfterEdit =
      editingImages.length > 0 ||
      editingNewImages.length > 0 ||
      !!editingPdfUrl ||
      !!editingNewPdf;

    if (!cleanText && !hasAttachmentsAfterEdit) {
      Alert.alert("Erreur", "Ajoute du texte ou garde au moins une pièce jointe.");
      return;
    }

    if (cleanText.length > 3000) {
      Alert.alert("Erreur", "Le post est trop long.");
      return;
    }

    if (!postEditHasChanges({
      post: editingPost,
      text: cleanText,
      retainedImageUrls: editingImages,
      newImageCount: editingNewImages.length,
      pdfUrl: editingPdfUrl,
      pdfName: editingPdfName,
      hasNewPdf: !!editingNewPdf,
    })) {
      closeEditPost();
      return;
    }

    let uploadedNewImages: Awaited<ReturnType<typeof uploadPostImages>> = [];
    let newPdfStoragePath: string | undefined;

    try {
      setSavingEdit(true);
      setTextOverlay((prev) => ({ ...prev, [editingPost.id]: cleanText }));

      validateCanonicalPostProfile(
        user.uid,
        user.email,
        profile as unknown as Record<string, unknown> | null
      );
      const editAuthSession = { token: await user.getIdToken() };

      uploadedNewImages =
        editingNewImages.length > 0
          ? await uploadPostImages({
              uid: user.uid,
              postId: editingPost.id,
              images: editingNewImages,
              authSession: editAuthSession,
            })
          : [];
      const imageEdit = buildPostImageEdit({
        post: editingPost,
        retainedUrls: editingImages,
        uploadedImages: uploadedNewImages,
      });
      let nextPdf: Awaited<ReturnType<typeof uploadPostPdf>> | null = null;
      if (editingNewPdf) {
        const storagePath = buildPostEditPdfStoragePath(user.uid, editingPost.id);
        newPdfStoragePath = storagePath;
        nextPdf = await uploadPostPdf({
          uid: user.uid,
          attachment: editingNewPdf,
          storagePath,
          authSession: editAuthSession,
        });
      }
      const editPdf = nextPdf
        ? {
            attachmentUrl: nextPdf.attachmentUrl,
            attachmentStoragePath: nextPdf.attachmentStoragePath,
            attachmentName: nextPdf.attachmentName,
          }
        : editingPdfUrl
        ? {
            attachmentUrl: editingPdfUrl,
            attachmentStoragePath: editingPost.attachmentStoragePath,
            attachmentName: editingPdfName,
          }
        : null;
      const updatePayload = buildPostEditPayload({
        text: cleanText,
        imageEdit,
        pdf: editPdf,
      });

      await updateDoc(doc(db, "posts", editingPost.id), updatePayload);
      closeEditPost();
      Alert.alert(
        "Modification enregistrée",
        "Ton post est de nouveau en révision. Il peut rester temporairement invisible du feed jusqu'à sa validation."
      );
    } catch (e: any) {
      if (uploadedNewImages.length > 0) {
        try {
          await cleanupCurrentPostImages({
            uid: user.uid,
            postId: editingPost.id,
            storagePaths: uploadedNewImages.map((image) => image.storagePath),
          });
        } catch (cleanupError) {
          console.error("edit-post:rollback:error", firebaseErrorDetails(cleanupError));
        }
      }
      if (newPdfStoragePath) {
        try {
          await cleanupCurrentPostPdf({
            uid: user.uid,
            postId: editingPost.id,
            storagePath: newPdfStoragePath,
          });
        } catch (cleanupError) {
          console.error("edit-post:rollback:error", {
            ...firebaseErrorDetails(cleanupError),
            stage: "delete-new-pdf",
            postId: editingPost.id,
            storagePath: newPdfStoragePath,
          });
        }
      }
      console.error("edit-post:error", {
        ...firebaseErrorDetails(e),
        stage: uploadedNewImages.length > 0 ? "firestore" : "profile-or-upload",
        postId: editingPost.id,
        storagePath: uploadedNewImages.at(-1)?.storagePath ?? null,
      });
      setTextOverlay((prev) => {
        const copy = { ...prev };
        delete copy[editingPost.id];
        return copy;
      });
      Alert.alert("Erreur", safePostErrorMessage(e, "Impossible de modifier le post."));
    } finally {
      setSavingEdit(false);
    }
  }, [
    closeEditPost,
    editingImages,
    editingNewImages,
    editingNewPdf,
    editingPdfName,
    editingPdfUrl,
    editingPost,
    editingText,
    profile,
    savingEdit,
    user,
  ]);

  const confirmDeleteSelectedPost = useCallback(() => {
    if (!selectedPost) return;
    const postId = selectedPost.id;
    setSelectedPost(null);
    Alert.alert("Supprimer ce post ?", "Cette action est irreversible.", [
      { text: "Annuler", style: "cancel" },
      {
        text: "Supprimer",
        style: "destructive",
        onPress: () => deletePost(postId),
      },
    ]);
  }, [deletePost, selectedPost]);

  const openReportForm = useCallback(() => {
    if (!selectedPost || !user || selectedPost.authorUid === user.uid) return;
    setReportingPost(selectedPost);
    setReportReason("other");
    setReportDetails("");
    setSelectedPost(null);
  }, [selectedPost, user]);

  const submitReport = useCallback(async () => {
    if (!reportingPost || !user || submittingReport) return;
    setSubmittingReport(true);
    try {
      await reportPost({ reporterUid: user.uid, postId: reportingPost.id, reason: reportReason, details: reportDetails });
      optimisticHide(reportingPost.id);
      void persistReportedPost(reportingPost.id);
      setReportingPost(null);
      Alert.alert("Signalement envoyé", "Merci. Ce contenu sera examiné.");
    } catch (e: any) {
      if (__DEV__) console.warn("[feed] post report failed", e?.code ?? "unknown");
      Alert.alert("Signalement impossible", "La publication n'a pas pu être signalée. Réessaie.");
    } finally {
      setSubmittingReport(false);
    }
  }, [optimisticHide, persistReportedPost, reportDetails, reportReason, reportingPost, submittingReport, user]);

  const blockSelectedAuthor = useCallback(async () => {
    if (!selectedPost || !user) return;
    const blockedUid = String(selectedPost.authorUid ?? "");
    if (!blockedUid || blockedUid === user.uid) return;

    try {
      await blockUser({ uid: user.uid, blockedUid });
      setSelectedPost(null);
      Alert.alert("Utilisateur bloqué", "Ses contenus seront masqués de ton fil.");
    } catch (e: any) {
      if (__DEV__) console.warn("[feed] block failed", e?.code ?? "unknown");
      Alert.alert("Action impossible", "Cet utilisateur n'a pas pu être bloqué. Réessaie.");
    }
  }, [selectedPost, user]);

  const toggleLike = useCallback(
    async (postId: string) => {
      const u = auth.currentUser;
      if (!u) return;

      const current = visiblePosts.find((p: any) => p.id === postId);
      const baseLikedBy: string[] = Array.isArray(current?.likedBy)
        ? current.likedBy
        : [];
      const baseLikes: number = typeof current?.likesCount === "number" && Number.isInteger(current.likesCount)
        ? current.likesCount
        : baseLikedBy.length;

      const alreadyLiked = likedPostIds.has(postId) || baseLikedBy.includes(u.uid);
      const nextLikedBy = alreadyLiked
        ? baseLikedBy.filter((x) => x !== u.uid)
        : [...baseLikedBy, u.uid];

      const nextLikes = alreadyLiked ? Math.max(0, baseLikes - 1) : baseLikes + 1;

      const prevOverlay = likesOverlay[postId];

      setLikesOverlay((prev) => ({
        ...prev,
        [postId]: { likedBy: nextLikedBy, likesCount: nextLikes },
      }));
      setLikedPostIds((prev) => {
        const next = new Set(prev);
        if (alreadyLiked) next.delete(postId);
        else next.add(postId);
        return next;
      });

      try {
        await togglePostLike({ postId, uid: u.uid, currentlyLiked: alreadyLiked });
      } catch {
        setLikedPostIds((prev) => {
          const next = new Set(prev);
          if (alreadyLiked) next.add(postId);
          else next.delete(postId);
          return next;
        });
        setLikesOverlay((prev) => {
          const copy = { ...prev };
          if (prevOverlay) copy[postId] = prevOverlay;
          else delete copy[postId];
          return copy;
        });
      }
    },
    [likedPostIds, likesOverlay, setLikedPostIds, visiblePosts]
  );

  const handleToggleLike = useCallback(
    async (postId: string) => {
      await toggleLike(postId);
    },
    [toggleLike]
  );

  const handleOpenPostActions = useCallback((item: any) => {
    openPostActions(item);
  }, [openPostActions]);

  const handleSharePost = useCallback((item: any) => {
    router.push({
      pathname: "/share/post/[postId]",
      params: { postId: item.id },
    });
  }, []);

  const keyExtractor = useCallback((item: MixedFeedItem<FeedPost>) => item.key, []);

  const renderPostItem = useCallback(
    ({ item }: { item: MixedFeedItem<FeedPost> }) =>
      item.type === "sponsored" ? (
        <SponsoredPostCard item={item} onPressCta={recordSponsoredClick} />
      ) : (
        <PostCard
          item={item.post}
          liked={item.post.liked}
          likes={item.post.likes}
          isMine={item.post.isMine}
          onToggleLike={handleToggleLike}
          onOpenActions={handleOpenPostActions}
          onSharePost={handleSharePost}
        />
      ),
    [handleToggleLike, handleOpenPostActions, handleSharePost, recordSponsoredClick]
  );

  const renderSubjectItem = useCallback(
    ({ item: s }: { item: (typeof subjects)[number] }) => {
      const active = selectedSubject === s;

      return (
        <Pressable
          accessibilityRole="radio"
          accessibilityLabel={`Filtrer par matière : ${s === "all" ? "Toutes" : SUBJECT_LABEL[s] ?? s}`}
          accessibilityHint="Les matières défilent horizontalement"
          accessibilityState={{ checked: active }}
          onPress={() => setSelectedSubject(s)}
          style={{
            minHeight: Platform.OS === "web" ? 32 : 40,
            paddingVertical: Platform.OS === "web" ? 4 : 8,
            paddingHorizontal: 12,
            borderRadius: 999,
            borderWidth: 1,
            borderColor: active ? ACCENT : surface.elevated,
            backgroundColor: active ? ACCENT : surface.primary,
          }}
        >
          <Text style={{ color: uiText.primary, fontWeight: "700" }}>
            {active ? "✓ " : ""}{s === "all" ? "Tous" : SUBJECT_LABEL[s] ?? s}
          </Text>
        </Pressable>
      );
    },
    [selectedSubject]
  );

  const handleEndReached = useCallback(() => {
    if (hasMore && !loadingMore) {
      void loadMore();
    }
  }, [hasMore, loadMore, loadingMore]);

  const retryFeed = useCallback(() => {
    reload();
  }, [reload]);

  const feedErrorMessage = offline
    ? "Tu es hors ligne. Vérifie ta connexion puis réessaie."
    : error;
  const canRetryFeed = offline || errorRetryable;

  const listFooter = useMemo(() => {
    if (feedErrorMessage && visiblePosts.length > 0 && !loadingMore) {
      return (
        <View style={{ alignItems: "center", paddingVertical: 14 }}>
          <Text style={{ color: uiText.danger, textAlign: "center" }}>
            {feedErrorMessage}
          </Text>
          {canRetryFeed ? (
            <Pressable
              onPress={retryFeed}
              accessibilityRole="button"
              accessibilityLabel="Réessayer de charger les posts"
              style={{ paddingVertical: 8, paddingHorizontal: 12 }}
            >
              <Text style={{ color: uiText.brand, fontWeight: "900" }}>Réessayer</Text>
            </Pressable>
          ) : null}
        </View>
      );
    }

    if (!loadingMore) return <View style={{ height: 8 }} />;

    return (
      <View style={{ paddingVertical: 16 }}>
        <ActivityIndicator color={uiText.brand} />
      </View>
    );
  }, [
    canRetryFeed,
    feedErrorMessage,
    loadingMore,
    retryFeed,
    visiblePosts.length,
  ]);

  const trimmedEditingText = editingText.trim();
  const totalEditingImages = editingImages.length + editingNewImages.length;
  const hasEditingPdf = !!editingPdfUrl || !!editingNewPdf;
  const canSaveEdit =
    !!editingPost &&
    !savingEdit &&
    (trimmedEditingText.length > 0 || totalEditingImages > 0 || hasEditingPdf) &&
    trimmedEditingText.length <= 3000 &&
    (
      trimmedEditingText !== String(editingPost.text ?? "").trim() ||
      editingNewImages.length > 0 ||
      editingImages.length !== getEditableImageUrls(editingPost).length ||
      editingImages.some((url, index) => getEditableImageUrls(editingPost)[index] !== url) ||
      editingNewPdf !== null ||
      (!!editingPdfUrl) !==
        (editingPost?.attachmentType === "pdf" &&
          String(editingPost?.attachmentUrl ?? "").length > 0) ||
      editingPdfUrl !==
        (editingPost?.attachmentType === "pdf" ? String(editingPost?.attachmentUrl ?? "") : "") ||
      editingPdfName !==
        (editingPost?.attachmentType === "pdf" ? String(editingPost?.attachmentName ?? "") : "") ||
      editingPost?.attachmentType === "image"
    );
  const selectedPostIsMine = !!user?.uid && selectedPost?.authorUid === user.uid;

  const localJobsHeader = localPublishJobs.length > 0 ? (
    <View style={{ gap: 10, paddingBottom: 12 }}>
      <View style={{ gap: 3 }}>
        <Text style={{ color: uiText.primary, fontWeight: "900", fontSize: 18 }}>Publications en cours</Text>
        <Text style={{ color: uiText.muted, lineHeight: 19 }}>
          Elles apparaîtront ici jusqu&apos;à leur envoi ou leur validation.
        </Text>
      </View>
      {localPublishJobs.map((job) => (
        <PendingPostCard
          key={job.jobId}
          job={job}
          onRetry={() => void retryPublish(job.jobId)}
          onCancel={() => void cancelPublish(job.jobId)}
          onRemove={() => void removePublish(job.jobId)}
        />
      ))}
    </View>
  ) : null;


  const feedHeader = (
      <View
        style={{
          alignSelf: "center",
          boxSizing: "border-box",
          maxWidth: contentMaxWidths.feed,
          paddingHorizontal: Platform.OS === "web" ? 0 : 16,
          paddingTop: 12,
          paddingBottom: 12,
          gap: 8,
          width: "100%",
        }}
      >
        <Text style={{ color: uiText.primary, fontSize: 28, fontWeight: "900" }}>
          Actualités
        </Text>

        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
          {([false, true] as const).map((isFollowing) => (
            <Pressable
              key={String(isFollowing)}
              accessibilityRole="radio"
              accessibilityLabel={isFollowing ? "Voir les publications des abonnements" : "Voir toutes les publications"}
              accessibilityState={{ checked: followingOnly === isFollowing }}
              onPress={() => setFollowingOnly(isFollowing)}
              style={{
                minHeight: Platform.OS === "web" ? 32 : 44,
                paddingVertical: Platform.OS === "web" ? 4 : 8,
                paddingHorizontal: 12,
                borderRadius: 999,
                borderWidth: 1,
                borderColor: followingOnly === isFollowing ? ACCENT : surface.elevated,
                backgroundColor: followingOnly === isFollowing ? surface.selected : surface.primary,
              }}
            >
              <Text style={{ color: uiText.primary, fontWeight: "800" }}>
                {followingOnly === isFollowing ? "✓ " : ""}{isFollowing ? "Abonnements" : "Tous les posts"}
              </Text>
            </Pressable>
          ))}
        </View>

        <View style={{ gap: 6 }}>
          <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <Text style={{ color: uiText.muted, fontSize: 12, fontWeight: "800" }}>Matières</Text>
          </View>
          <FlatList
            horizontal
            style={{ width: "100%" }}
            showsHorizontalScrollIndicator={false}
            data={subjects}
            keyExtractor={(s) => s}
            contentContainerStyle={{ gap: 10, paddingRight: 16 }}
            renderItem={renderSubjectItem}
            accessibilityLabel="Filtres de matières"
          />
        </View>

        <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <Pressable
            onPress={() => setTeachersOnly((current) => !current)}
            accessibilityRole="switch"
            accessibilityLabel="Afficher uniquement les publications des professeurs"
            accessibilityState={{ checked: teachersOnly }}
            style={{
              minHeight: Platform.OS === "web" ? 32 : 44,
              paddingVertical: Platform.OS === "web" ? 4 : 7,
              paddingHorizontal: 12,
              borderRadius: 999,
              borderWidth: 1,
              borderColor: teachersOnly ? ACCENT : surface.elevated,
              backgroundColor: teachersOnly ? surface.selected : surface.primary,
            }}
          >
            <Text style={{ color: uiText.primary, fontWeight: "800" }}>
              {teachersOnly ? "Professeurs seulement" : "Tous les profils"}
            </Text>
          </Pressable>

          <Text style={{ color: uiText.muted, flexShrink: 1, textAlign: "right" }}>
            {visiblePosts.length === 0
              ? "Aucun post"
              : `${visiblePosts.length} publication${visiblePosts.length > 1 ? "s" : ""}`}
            {localPublishJobs.length > 0 ? ` · ${localPublishJobs.length} en cours` : ""}
          </Text>
        </View>
      </View>
  );

  if (
    loadingProfile ||
    (loadingPosts &&
      posts.length === 0 &&
      localPublishJobs.length === 0 &&
      !feedErrorMessage)
  ) {
    return (
      <SafeAreaView edges={["top", "left", "right"]} style={{ flex: 1, backgroundColor: BG }}>
        <View style={{ flex: 1, justifyContent: "center", alignItems: "center", gap: 10 }}>
          <ActivityIndicator color={uiText.brand} />
          <Text accessibilityLiveRegion="polite" style={{ color: uiText.muted }}>Chargement…</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView edges={["top", "left", "right"]} style={{ flex: 1, backgroundColor: BG }}>
      {Platform.OS !== "web" ? feedHeader : null}

      <FlatList
        style={{ flex: 1, width: "100%", minHeight: 0 }}
        data={feedItems}
        keyExtractor={keyExtractor}
        renderItem={renderPostItem}
        onViewableItemsChanged={onViewableItemsChanged}
        viewabilityConfig={SPONSORED_VISIBILITY_CONFIG}
        ListHeaderComponent={<>{Platform.OS === "web" ? feedHeader : null}{localJobsHeader}</>}
        contentContainerStyle={{ ...readingContent(contentMaxWidths.feed), paddingHorizontal: 16, paddingBottom: 24 }}
        ItemSeparatorComponent={() => <View style={{ height: 12 }} />}
        ListEmptyComponent={
          localPublishJobs.length > 0 ? null : loadingPosts ? (
            <View style={{ alignItems: "center", paddingTop: 24 }}>
              <ActivityIndicator color={uiText.brand} />
              <Text accessibilityLiveRegion="polite" style={{ color: uiText.muted, marginTop: 8 }}>Chargement…</Text>
            </View>
          ) : feedErrorMessage ? (
            <View
              style={{
                marginTop: 24,
                padding: 16,
                borderRadius: 16,
                borderWidth: 1,
                borderColor: border.danger,
                backgroundColor: surface.danger,
                alignItems: "center",
              }}
            >
              <Text style={{ color: uiText.danger, fontWeight: "800", textAlign: "center" }}>
                {feedErrorMessage}
              </Text>
              {canRetryFeed ? (
                <Pressable
                  onPress={reload}
                  accessibilityRole="button"
                  accessibilityLabel="Réessayer de charger le fil"
                  style={{ marginTop: 12, paddingHorizontal: 14, paddingVertical: 10 }}
                >
                  <Text style={{ color: uiText.brand, fontWeight: "900" }}>Réessayer</Text>
                </Pressable>
              ) : null}
            </View>
          ) : (
            <Text style={{ color: uiText.muted, textAlign: "center", marginTop: 24 }}>
              {followingOnly
                ? "Aucun post des comptes suivis pour le moment."
                : "Aucun post pour ce filtre."}
            </Text>
          )
        }
        ListFooterComponent={listFooter}
        refreshing={loadingPosts && posts.length > 0}
        onRefresh={reload}
        initialNumToRender={6}
        maxToRenderPerBatch={6}
        windowSize={7}
        updateCellsBatchingPeriod={50}
        removeClippedSubviews={Platform.OS === "android"}
        onEndReached={handleEndReached}
        onEndReachedThreshold={0.6}
      />

      <Modal
        visible={!!selectedPost}
        transparent
        animationType="fade"
        onRequestClose={closeActionSheet}
      >
        <Pressable
          onPress={closeActionSheet}
                  accessibilityRole="button"
          style={{
            flex: 1,
            backgroundColor: surface.overlay,
            justifyContent: Platform.OS === "web" ? "center" : "flex-end",
            alignItems: "center",
            padding: Platform.OS === "web" ? 16 : 0,
          }}
        >
          <Pressable
            onPress={() => {}}
            style={{
              width: "100%",
              maxWidth: Platform.OS === "web" ? contentMaxWidths.dialog : undefined,
              borderTopLeftRadius: 22,
              borderTopRightRadius: 22,
              backgroundColor: surface.primary,
              borderTopWidth: 1,
              borderLeftWidth: 1,
              borderRightWidth: 1,
              borderColor: border.subtle,
              padding: 16,
              gap: 10,
            }}
          >
            <View
              style={{
                alignSelf: "center",
                width: 44,
                height: 5,
                borderRadius: 999,
                backgroundColor: surface.elevated,
              }}
            />

            <View
              style={{
                borderRadius: 18,
                backgroundColor: surface.primary,
                borderWidth: 1,
                borderColor: border.subtle,
                padding: 10,
              }}
            >
              <Text style={{ color: uiText.muted }} numberOfLines={2}>
                {String(selectedPost?.text ?? "Choisis une action pour ce post.")}
              </Text>
            </View>

            {selectedPostIsMine ? (
              <>
                <Pressable
                  onPress={openEditPost}
                  accessibilityRole="button"
                  style={{
                    borderRadius: 16,
                    paddingHorizontal: 14,
                    paddingVertical: 14,
                    backgroundColor: surface.selected,
                    borderWidth: 1,
                    borderColor: border.brand,
                  }}
                >
                  <Text style={{ color: uiText.primary, fontWeight: "900" }}>Modifier</Text>

                </Pressable>

                <Pressable
                  onPress={confirmDeleteSelectedPost}
                  accessibilityRole="button"
                  style={{
                    borderRadius: 16,
                    paddingHorizontal: 14,
                    paddingVertical: 14,
                    backgroundColor: surface.danger,
                    borderWidth: 1,
                    borderColor: border.danger,
                  }}
                >
                  <Text style={{ color: uiText.danger, fontWeight: "900" }}>Supprimer</Text>

                </Pressable>
              </>
            ) : (
              <>
                <Pressable
                  onPress={openReportForm}
                  accessibilityRole="button"
                  style={{
                    borderRadius: 16,
                    paddingHorizontal: 14,
                    paddingVertical: 14,
                    backgroundColor: surface.warning,
                    borderWidth: 1,
                    borderColor: brand.accent,
                  }}
                >
                  <Text style={{ color: brand.accent, fontWeight: "900" }}>Signaler</Text>

                </Pressable>

                <Pressable
                  onPress={blockSelectedAuthor}
                  accessibilityRole="button"
                  style={{
                    borderRadius: 16,
                    paddingHorizontal: 14,
                    paddingVertical: 14,
                    backgroundColor: surface.danger,
                    borderWidth: 1,
                    borderColor: border.danger,
                  }}
                >
                  <Text style={{ color: uiText.danger, fontWeight: "900" }}>Bloquer</Text>

                </Pressable>
              </>
            )}

            <Pressable
              onPress={closeActionSheet}
              style={{
                alignItems: "center",
                paddingVertical: 10,
              }}
            >
              <Text style={{ color: uiText.muted, fontWeight: "800" }}>Annuler</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>

      <Modal
        visible={!!reportingPost}
        transparent
        animationType="fade"
        onRequestClose={() => { if (!submittingReport) setReportingPost(null); }}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : "height"}
          style={{ flex: 1, backgroundColor: surface.overlay, justifyContent: "center", padding: 16 }}
        >
<ScrollView keyboardShouldPersistTaps="handled" contentContainerStyle={{ flexGrow: 1, justifyContent: "center", alignItems: "center" }}>
          <View style={{ width: "100%", maxWidth: Platform.OS === "web" ? contentMaxWidths.dialog : undefined, borderRadius: 22, backgroundColor: surface.primary, borderWidth: 1, borderColor: border.subtle, padding: 18, gap: 12 }}>
            <Text style={{ color: uiText.primary, fontSize: 20, fontWeight: "900" }}>Signaler la publication</Text>
            <Text style={{ color: uiText.muted }}>Choisis une raison. Ton identité ne sera pas communiquée à l&apos;auteur.</Text>
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
              {REPORT_REASONS.map((reason) => (
                <Pressable key={reason.value} onPress={() => setReportReason(reason.value)} style={{ paddingHorizontal: 10, paddingVertical: 8, borderRadius: 999, backgroundColor: reportReason === reason.value ? brand.primary : surface.primary }}>
                  <Text style={{ color: uiText.primary, fontWeight: "800" }}>{reason.label}</Text>
                </Pressable>
              ))}
            </View>
            <BrandTextInput
              value={reportDetails}
              onChangeText={setReportDetails}
              maxLength={500}
              placeholder="Commentaire facultatif"
              placeholderTextColor={uiText.muted}
              multiline
              style={{ color: uiText.primary, minHeight: 80, borderRadius: 14, borderWidth: 1, borderColor: border.subtle, padding: 12, textAlignVertical: "top" }}
            />
            <View style={{ flexDirection: "row", justifyContent: "flex-end", gap: 10 }}>
              <Pressable disabled={submittingReport} onPress={() => setReportingPost(null)} style={{ padding: 12 }}><Text style={{ color: uiText.muted, fontWeight: "800" }}>Annuler</Text></Pressable>
              <Pressable disabled={submittingReport} onPress={() => void submitReport()} style={{ paddingHorizontal: 16, paddingVertical: 12, borderRadius: 999, backgroundColor: brand.primary, opacity: submittingReport ? 0.6 : 1 }}><Text style={{ color: uiText.primary, fontWeight: "900" }}>{submittingReport ? "Envoi…" : "Envoyer"}</Text></Pressable>
            </View>
          </View>
        </ScrollView>
</KeyboardAvoidingView>
      </Modal>

      <Modal
        visible={!!editingPost}
        transparent
        animationType="fade"
        onRequestClose={closeEditPost}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : "height"}
          style={{
            flex: 1,
            backgroundColor: surface.overlay,
            justifyContent: "center",
            padding: 16,
          }}
        >
<ScrollView keyboardShouldPersistTaps="handled" contentContainerStyle={{ flexGrow: 1, justifyContent: "center" }}>
          <Pressable
            onPress={closeEditPost}
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
            }}
          />
          <View
            style={{
              ...readingContent(contentMaxWidths.create),
              borderRadius: 22,
              backgroundColor: surface.primary,
              borderWidth: 1,
              borderColor: border.subtle,
              padding: 18,
              gap: 14,
            }}
          >
            <View>
              <Text style={{ color: uiText.primary, fontSize: 20, fontWeight: "900" }}>
                Modifier le post
              </Text>
              <Text style={{ color: uiText.muted, marginTop: 6 }}>
                {supportsMediaEditing ? "Texte, photos et PDF." : "L’édition Web conserve les médias existants. Pour les remplacer, crée une nouvelle publication."}
              </Text>
            </View>

            <View
              style={{
                borderRadius: 18,
                borderWidth: 1,
                borderColor: border.subtle,
                backgroundColor: surface.primary,
                padding: 12,
              }}
            >
              <BrandTextInput
                value={editingText}
                onChangeText={setEditingText}
                placeholder="Écris ton post…"
                placeholderTextColor={uiText.muted}
                multiline
                maxLength={3000}
                style={{
                  minHeight: 140,
                  maxHeight: 240,
                  color: uiText.primary,
                  fontSize: 15,
                  lineHeight: 22,
                  textAlignVertical: "top",
                }}
              />
            </View>

            <View style={{ gap: 10 }}>
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text style={{ color: uiText.primary, fontWeight: "800" }}>Photos</Text>
                <Pressable
                  disabled={!supportsMediaEditing}
                      accessibilityState={{ disabled: !supportsMediaEditing }}
                      onPress={pickEditImages}
                  style={{
                    borderRadius: 12,
                    paddingHorizontal: 12,
                    paddingVertical: 8,
                    backgroundColor: surface.selected,
                    borderWidth: 1,
                    borderColor: border.brand,
                  }}
                >
                  <Text style={{ color: uiText.primary, fontWeight: "800" }}>Ajouter</Text>
                </Pressable>
              </View>

              {totalEditingImages > 0 ? (
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  <View style={{ flexDirection: "row", gap: 10 }}>
                    {editingImages.map((imageUrl, index) => (
                      <View key={`existing_${imageUrl}_${index}`}>
                        <Image
                          source={{ uri: imageUrl }}
                          style={{
                            width: 90,
                            height: 90,
                            borderRadius: 14,
                            backgroundColor: surface.primary,
                          }}
                        />
                        <Pressable
                          onPress={() => removeExistingEditImageAt(index)}
                          style={{
                            position: "absolute",
                            top: 6,
                            right: 6,
                            backgroundColor: surface.overlay,
                            borderRadius: 999,
                            paddingHorizontal: 8,
                            paddingVertical: 4,
                          }}
                        >
                          <Text style={{ color: uiText.primary, fontSize: 12, fontWeight: "900" }}>
                            Retirer
                          </Text>
                        </Pressable>
                      </View>
                    ))}

                    {editingNewImages.map((image, index) => (
                      <View key={`new_${image.uri}_${index}`}>
                        <Image
                          source={{ uri: image.uri }}
                          style={{
                            width: 90,
                            height: 90,
                            borderRadius: 14,
                            backgroundColor: surface.primary,
                            opacity: 0.95,
                          }}
                        />
                        <View
                          style={{
                            position: "absolute",
                            left: 6,
                            bottom: 6,
                            backgroundColor: surface.selected,
                            borderRadius: 999,
                            paddingHorizontal: 8,
                            paddingVertical: 4,
                          }}
                        >
                          <Text style={{ color: uiText.primary, fontSize: 11, fontWeight: "900" }}>
                            Nouvelle
                          </Text>
                        </View>
                        <Pressable
                          onPress={() => removeNewEditImageAt(index)}
                          style={{
                            position: "absolute",
                            top: 6,
                            right: 6,
                            backgroundColor: surface.overlay,
                            borderRadius: 999,
                            paddingHorizontal: 8,
                            paddingVertical: 4,
                          }}
                        >
                          <Text style={{ color: uiText.primary, fontSize: 12, fontWeight: "900" }}>
                            Retirer
                          </Text>
                        </Pressable>
                      </View>
                    ))}
                  </View>
                </ScrollView>
              ) : (
                <Text style={{ color: uiText.muted }}>Aucune photo pour ce post.</Text>
              )}
            </View>

            {canPublishDocuments(profile) ? (
              <View style={{ gap: 10 }}>
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text style={{ color: uiText.primary, fontWeight: "800" }}>PDF</Text>
                  <View style={{ flexDirection: "row", gap: 8 }}>
                    <Pressable
                      disabled={!supportsMediaEditing}
                      accessibilityState={{ disabled: !supportsMediaEditing }}
                      onPress={pickEditPdf}
                      style={{
                        borderRadius: 12,
                        paddingHorizontal: 12,
                        paddingVertical: 8,
                        backgroundColor: surface.selected,
                        borderWidth: 1,
                        borderColor: border.brand,
                      }}
                    >
                      <Text style={{ color: uiText.primary, fontWeight: "800" }}>
                        {hasEditingPdf ? "Remplacer" : "Ajouter"}
                      </Text>
                    </Pressable>

                    {hasEditingPdf ? (
                      <Pressable
                        onPress={clearEditPdf}
                        style={{
                          borderRadius: 12,
                          paddingHorizontal: 12,
                          paddingVertical: 8,
                          backgroundColor: surface.danger,
                          borderWidth: 1,
                          borderColor: border.danger,
                        }}
                      >
                        <Text style={{ color: uiText.danger, fontWeight: "800" }}>Retirer</Text>
                      </Pressable>
                    ) : null}
                  </View>
                </View>

                {hasEditingPdf ? (
                  <View
                    style={{
                      borderRadius: 14,
                      borderWidth: 1,
                      borderColor: border.subtle,
                      backgroundColor: surface.primary,
                      padding: 12,
                    }}
                  >
                    <Text style={{ color: uiText.primary, fontWeight: "800" }} numberOfLines={1}>
                      {editingNewPdf?.name || editingPdfName || "Document PDF"}
                    </Text>
                    <Text style={{ color: uiText.muted, marginTop: 4 }}>
                      {editingNewPdf ? "Sera televerse a l'enregistrement" : "PDF conserve"}
                    </Text>
                  </View>
                ) : (
                  <Text style={{ color: uiText.muted }}>Aucun PDF pour ce post.</Text>
                )}
              </View>
            ) : null}

            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Text
                style={{
                  color:
                    trimmedEditingText.length > 3000 ? uiText.danger : uiText.muted,
                }}
              >
                {editingText.length}/3000
              </Text>

              {!canSaveEdit && trimmedEditingText.length > 0 ? (
                <Text style={{ color: uiText.muted }}>
                  {trimmedEditingText === String(editingPost?.text ?? "").trim()
                    ? "Aucune modification"
                    : "Verifie le texte"}
                </Text>
              ) : null}
            </View>

            <View style={{ flexDirection: "row", gap: 10 }}>
              <Pressable
                onPress={closeEditPost}
                disabled={savingEdit}
                style={{
                  flex: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 14,
                  borderWidth: 1,
                  borderColor: border.subtle,
                  backgroundColor: surface.primary,
                  paddingVertical: 14,
                }}
              >
                <Text style={{ color: uiText.primary, fontWeight: "800" }}>Annuler</Text>
              </Pressable>

              <Pressable
                onPress={submitEditPost}
                disabled={!canSaveEdit}
                style={{
                  flex: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 14,
                  backgroundColor: canSaveEdit ? ACCENT : surface.selected,
                  paddingVertical: 14,
                }}
              >
                <Text style={{ color: uiText.primary, fontWeight: "900" }}>
                  {savingEdit ? "Enregistrement…" : "Enregistrer"}
                </Text>
              </Pressable>
            </View>
          </View>
        </ScrollView>
</KeyboardAvoidingView>
      </Modal>
    </SafeAreaView>
  );
}
